import { useEffect, useState, Fragment } from "react";
import { Button, Input } from "@mui/material";
import { Scrollbars } from "react-custom-scrollbars";
import { defaultViewProgram, optionsTypes } from "constants/viewer";
import LayerIcon from "components/LayerIcon";
import {
    getLayerOptions,
    getLayerType,
    getInputsMesh,
    getEntityBounds,
    getMeshProperties,
    updateMeshProperties,
    getEntitiesId,
} from "actions/GlobalActions";
import { createGuid, uniqueArray } from "services/UtilsService";
import { handleLayerEvent, handleLayers } from "actions/ViewerTools";
import { PanelHeader, ConfirmationDialog } from "components";
import AttributeQuery from "./AttributeQuery";
import DrillHoleSettings from "./DrillHoleSettings";
import RasterImagery from "./RasterImagery";
import ImageryPlane from "./ImageryPlane";
import ColorLegend from "./ColorLegend";
import BlockModelLimits from "./BlockModelLimits";
import RasterProperties from "./RasterProperties";
import { ViewerActions, RasterActions, LayersActions } from "actions";
import { VisibilityOff, CenterFocusStrong, RemoveCircle } from "@mui/icons-material";
import { LayersDuplicate } from "assets/images/index";
import LayerOpacity from "assets/images/ic_opacity.svg";
import commonStyles from "styles/common";
import useStyles from "styles/layerProperties";
import InputsPanel from "./InputsPanel";

function renderOptions(options: any, settings: any) {
    //console.log('options:', options);
    var { layer, isPlane, isColorLegend, entities, isBlockModel, props } = settings;
    var { selectedLayers } = props;
    var isMultiple = Object.values(selectedLayers).length > 1;
    var { attributes, drillHoles } = entities;
    var attributeQueryEntity = attributes.join(" ");
    var drillHoleSettingsEntity = drillHoles.join(" ");
    return (
        <Fragment>
            <InputsPanel layer={layer} />
            {drillHoleSettingsEntity && options.includes(optionsTypes.drillHoleSettings) ? (
                <DrillHoleSettings
                    layer={layer}
                    attributeQueryEntity={attributeQueryEntity}
                    drillHoleSettingsEntity={drillHoleSettingsEntity}
                    {...props}
                />
            ) : (
                ""
            )}
            {options.includes(optionsTypes.attributeFilter) ? (
                <AttributeQuery layer={layer} attributeQueryEntity={attributeQueryEntity} {...props} />
            ) : (
                ""
            )}
            {isPlane && options.includes(optionsTypes.imageryPlane) ? <ImageryPlane layer={layer} {...props} /> : ""}
            {isBlockModel && ViewerActions.hasBounds() && options.includes(optionsTypes.blockModelLimits) ? (
                <BlockModelLimits layer={layer} {...props} />
            ) : (
                ""
            )}
            {isColorLegend && options.includes(optionsTypes.colorLegend) ? (
                <ColorLegend layer={layer} attributeQueryEntity={attributeQueryEntity} {...props} />
            ) : (
                ""
            )}
            {!isMultiple && options.includes(optionsTypes.rasterImagery) ? (
                <RasterImagery layer={layer} {...props} />
            ) : (
                ""
            )}
        </Fragment>
    );
}

var layer: any, limboLayer: any;

function getOptions(selectedLayers: any, inputs: any, currentProject: any) {
    var currentOptions: any = [];
    var layersTypes: any = [];
    Object.values(selectedLayers).forEach((layer: any) => {
        var layerType = getLayerType(layer, inputs, currentProject);
        var layerOptions = getLayerOptions(layerType);
        if (layerOptions) {
            if (currentOptions.length === 0 && Object.values(layersTypes).length === 0) {
                currentOptions = layerOptions;
            } else {
                currentOptions = layerOptions.filter((value: any) => currentOptions.includes(value));
            }
        }
        layersTypes[layerType] = 0;
    });
    return currentOptions;
}

function getSettings(props: any) {
    var { selectedLayers, inputs } = props;
    var isMultiple = Object.values(selectedLayers).length > 1;
    var settings = {};
    var isColorLegend = false;
    const isPlane = layer["view_type"] === defaultViewProgram.IDXORTHO;
    const isBlockModel = layer["view_type"] === defaultViewProgram.VOXBM;
    var entities: any = {
        drillHoles: [],
        attributes: [],
        query: [],
    };
    if (inputs) {
        if (isMultiple) {
            var viewTypes = Object.keys(layer).filter((prop) => prop.includes("view_type"));
            viewTypes.forEach((viewType: string) => {
                isColorLegend = Boolean(Object.values(inputs[layer[viewType]]).find((elem: any) => elem.type === "7"));
                entities.attributes = uniqueArray(
                    entities.attributes.concat(getEntitiesId(layer, inputs, layer[viewType], "3"))
                );
                entities.query = uniqueArray(
                    entities.attributes.concat(getEntitiesId(layer, inputs, layer[viewType], "5"))
                );
                entities.drillHoles = uniqueArray(
                    entities.drillHoles.concat(getEntitiesId(layer, inputs, layer[viewType], "8"))
                );
            });
        } else {
            entities.attributes = getEntitiesId(layer, inputs, layer["view_type"], "3");
            entities.query = getEntitiesId(layer, inputs, layer["view_type"], "5");
            entities.drillHoles = getEntitiesId(layer, inputs, layer["view_type"], "8");
            if (inputs[layer.view_type]) {
                isColorLegend = Boolean(Object.values(inputs[layer.view_type]).find((elem: any) => elem.type === "7"));
            }
        }
    }
    //console.log('entities:', entities);
    settings = {
        ...settings,
        layer: layer,
        isPlane: isPlane,
        isBlockModel: isBlockModel,
        isColorLegend: isColorLegend,
        entities: entities,
        props: props,
    };
    return settings;
}

type IProps = {
    selectedLayers: any;
    currentProject: any;
    inputs: any;
    handleClose: () => void;
    getGradientImage: (gradientId: string) => "";
    toggleVisibilty: (visible: boolean) => void;
    setTransparency: (value: number) => void;
    deleteLayer: (layer: any) => void;
    updateParent: () => void;
    confirmDeleteLayer?: () => void;
    zoomEntities?: any;
    style?: any;
};

function Toolbar(props: IProps) {
    const { classes } = useStyles();
    var common = commonStyles();
    var { selectedLayers, currentProject, updateParent, confirmDeleteLayer, zoomEntities } = props;
    const isMultiple = selectedLayers.length > 1;
    const not_selectable = Boolean(layer.not_selectable);
    var meshProperty = getMeshProperties(layer);
    var isVisible = layer.layer_visible ? layer.layer_visible === "1" : true;
    var opacity = Math.floor(layer.layer_opacity ? parseFloat(layer.layer_opacity) * 100 : 100);
    if (isMultiple) {
        var hiddenLayers = selectedLayers.filter((layer: any) => {
            return layer.layer_visible === "0";
        });
        if (hiddenLayers.length >= selectedLayers.length / 2) {
            isVisible = false;
        }
    }

    if (layer["ortho_list"]) {
        var ortho_list = layer["ortho_list"].split(" ");
        ortho_list.forEach((ortho: string) => {
            zoomEntities.push(ortho);
        });
    }

    const zoomToLayer = () => {
        getEntityBounds(zoomEntities, currentProject, (bounds: any) => {
            console.log("bounds:", bounds);
            handleLayerEvent("frameWorldBounds", bounds, layer);
        });
    };

    const toggleVisibility = () => {
        if (isMultiple) {
            selectedLayers.forEach((layer: any) => {
                if (typeof layer.layer_visible === "string") {
                    layer["layer_visible"] = hiddenLayers.length >= selectedLayers.length / 2 ? "1" : "0";
                } else {
                    layer["layer_visible"] = "0";
                }
                handleLayerEvent("toggleVisibility", null, layer);
            });
            updateParent();
        } else {
            if (layer.not_selectable) {
                var newProp = {
                    ...meshProperty,
                    meshIndex: layer.index,
                    meshVisible: !meshProperty.meshVisible,
                };
                layer["layer_visible"] = !newProp.meshVisible ? "0" : "1";
                updateMeshProperties(layer, newProp);
            } else {
                props.toggleVisibilty(!isVisible);
            }
        }
    };

    const setTransparency = (event: any) => {
        if (isMultiple) {
            const opacity = parseInt(event.target.value) / 100;
            selectedLayers.forEach((layer: any) => {
                layer["layer_opacity"] = opacity.toString();
                handleLayerEvent("setTransparency", null, layer);
            });
            updateParent();
        } else {
            if (layer.not_selectable) {
                var opacity: number = parseInt(event.target.value);
                var newProp = {
                    ...meshProperty,
                    meshIndex: layer.index,
                    meshOpacity: opacity * 0.01,
                };
                layer["layer_opacity"] = newProp.meshOpacity + "";
                updateMeshProperties(layer, newProp);
                updateParent();
            } else {
                props.setTransparency(event.target.value);
            }
        }
    };

    return (
        <div className={classes.toolbar}>
            <Button size="small" className={classes.borderNone} disabled={not_selectable}>
                <LayersDuplicate title="Duplicate" className={common.disabledEvents} />
            </Button>
            <Button size="small" className={classes.borderNone} onClick={toggleVisibility}>
                <VisibilityOff titleAccess="Show/Hide" className={isVisible ? common.shaded : classes.grey} />
            </Button>
            <Button className={classes.borderNone} onClick={zoomToLayer}>
                <CenterFocusStrong titleAccess="Zoom to Layer" className={common.shaded} />
            </Button>
            <Button className={classes.borderNone} onClick={confirmDeleteLayer} disabled={not_selectable}>
                <RemoveCircle titleAccess="Delete" className={common.shaded} />
            </Button>
            <div
                style={{
                    borderLeft: "solid 1px #E6E6E6",
                    display: "inline-block",
                    paddingLeft: 15,
                    paddingTop: 5,
                }}
            >
                <img src={LayerOpacity} alt="Layer opacity" style={{ position: "relative", top: 4, right: 6 }} />
                <Input
                    type="number"
                    id="layer_properties_opacity"
                    className={classes.layerOpacityInput}
                    value={opacity}
                    disableUnderline={true}
                    inputProps={{
                        min: 0,
                        max: 100,
                        step: 1,
                    }}
                    onChange={setTransparency}
                />
            </div>
        </div>
    );
}

export default function LayerProperties(props: IProps) {
    const { classes } = useStyles();
    var { selectedLayers, currentProject, inputs, handleClose, style } = props;
    const isMultiple = selectedLayers.length > 1;
    if (isMultiple) {
        if (!layer || layer === null || layer.name !== "Multiple Objects Selected") {
            if (limboLayer) {
                layer = { ...limboLayer };
            } else {
                layer = { name: "Multiple Objects Selected", ID: createGuid() };
            }
        }
        selectedLayers.forEach((currentLayer: any, index: number) => {
            var inputsInfo = getInputsMesh(currentLayer, inputs);
            if (inputsInfo) {
                layer["input_value_" + inputsInfo.id] = inputsInfo.value;
            }
            if (currentLayer["view_type"]) {
                layer["view_type_" + index] = currentLayer["view_type"];
            }
        });
    } else {
        layer = selectedLayers[0];
    }
    //console.log('layer:', layer);
    var currentOptions = getOptions(selectedLayers, inputs, currentProject);
    var settings: any = getSettings(props);
    //console.log("options:", currentOptions, "|settings:", settings);
    var { attributes } = settings.entities;

    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [raster, setRaster] = useState(null);
    const [update, setUpdate] = useState(false);
    console.log(update)

    var onUpdate = (layer: any) => {
        //console.log('update.LayerProperties', layer);
        setUpdate((prevUpate) => !prevUpate);
    };

    const openRasterProperties = (raster: any) => {
        setRaster(raster);
    };

    useEffect(() => {
        RasterActions.setSelected(null);
    }, [selectedLayers]);

    useEffect(() => {
        RasterActions.on("setRaster", openRasterProperties);
        LayersActions.on("updateLayer", onUpdate);
        return () => {
            RasterActions.removeEventListener("setRaster", openRasterProperties);
            LayersActions.removeEventListener("updateLayer", onUpdate);
        };
    }, []);

    const confirmDeleteLayer = () => {
        setOpenConfirmation(true);
    };

    const confirmAction = (object: any) => {
        if (object.status) {
            if (isMultiple) {
                selectedLayers.forEach((layer: any) => {
                    //console.log('layer', layer);
                    handleLayerEvent("deleteLayer", null, layer);
                    var layers = LayersActions.layers;
                    delete layers[layer.ID];
                    handleLayers({ ...layers });
                    LayersActions.needsUpdate = true;
                    LayersActions.emit("updateTree", layers);
                });
            } else {
                props.deleteLayer(layer);
            }
            setOpenConfirmation(false);
        } else {
            setOpenConfirmation(false);
        }
    };

    const handleRasterClose = () => {
        setRaster(null);
        RasterActions.emit("closeRaster", "");
    };

    return (
        <div className={classes.root} style={style}>
            <PanelHeader icon={<LayerIcon layer={layer} {...props} />} title={layer.name} handleClose={handleClose} />
            <Toolbar zoomEntities={attributes} confirmDeleteLayer={confirmDeleteLayer} {...props} />
            <Scrollbars
                autoHide
                autoHideTimeout={1000}
                autoHeight
                autoHeightMax={"calc(100vh - 132px)"}
                style={{ width: "300", overflowX: "hidden" }}
            >
                {renderOptions(currentOptions, settings)}
            </Scrollbars>
            {openConfirmation ? (
                <ConfirmationDialog
                    open={openConfirmation}
                    title="Delete Objects"
                    type="objects"
                    name={selectedLayers.map((layer: any) => layer.name).join(", ")}
                    confirmAction={confirmAction}
                />
            ) : (
                ""
            )}
            {raster !== null ? (
                <RasterProperties layer={layer} raster={raster} handleRasterClose={handleRasterClose} {...props} />
            ) : (
                ""
            )}
        </div>
    );
}
